/**
 * Warranty Claim Edit Application
 * Handles loading, editing, and updating warranty claims
 */
(function() {
    'use strict';

    /**
     * Field mapping configuration
     * Maps API response fields to form selectors with optional transforms
     * 
     * EASY TO ADJUST: Modify this object to change field mappings
     * - apiField: The field name in the API response
     * - formSelector: The CSS selector for the form input
     * - isLineItem: If true, the field is in eShopCnctBWServiceOrderLineNew[0]
     * - transform: Optional transform function name ('splitFirstName', 'splitLastName', 'formatDate', 'formatPrice')
     * - readonly: If true, the field is not editable (just stored)
     */
    const FIELD_MAPPING = {
        // Root level fields - stored for reference
        orderId: { 
            apiField: 'no', 
            formSelector: '#order-id', 
            readonly: true 
        },
        
        // Product information
        description: { 
            apiField: 'description', 
            formSelector: '#product-description' 
        },
        
        // Contact information - name is split from single field
        firstName: { 
            apiField: 'name', 
            formSelector: '#first-name', 
            transform: 'splitFirstName' 
        },
        lastName: { 
            apiField: 'name', 
            formSelector: '#last-name', 
            transform: 'splitLastName' 
        },
        phone: { 
            apiField: 'phoneNo', 
            formSelector: '#phone' 
        },
        email: { 
            apiField: 'eMail', 
            formSelector: '#email' 
        },
        
        // Address fields
        address: { 
            apiField: 'address', 
            formSelector: '#street-address' 
        },
        city: { 
            apiField: 'city', 
            formSelector: '#city' 
        },
        county: { 
            apiField: 'county', 
            formSelector: '#state-province' 
        },
        postCode: { 
            apiField: 'postCode', 
            formSelector: '#zip-postal' 
        },
        country: { 
            apiField: 'shiptoCountryRegionCode', 
            formSelector: '#country' 
        },
        
        // Line item fields (from eShopCnctBWServiceOrderLineNew[0])
        serialNumber: { 
            apiField: 'batch', 
            formSelector: '#serial-number', 
            isLineItem: true 
        },
        purchaseDate: { 
            apiField: 'purchasedDate', 
            formSelector: '#purchase-date', 
            isLineItem: true, 
            transform: 'formatDate' 
        },
        store: { 
            apiField: 'store', 
            formSelector: '#purchase-store', 
            isLineItem: true 
        },
        price: { 
            apiField: 'price', 
            formSelector: '#product-price', 
            isLineItem: true,
            transform: 'formatPrice'
        }
    };

    /**
     * Transform functions for field values
     */
    const TRANSFORMS = {
        /**
         * Extract first name from full name (first word before first space)
         */
        splitFirstName: (value) => {
            if (!value) return '';
            const parts = value.trim().split(' ');
            return parts[0] || '';
        },
        
        /**
         * Extract last name from full name (everything after first space)
         */
        splitLastName: (value) => {
            if (!value) return '';
            const parts = value.trim().split(' ');
            return parts.slice(1).join(' ') || '';
        },
        
        /**
         * Convert ISO date (YYYY-MM-DD) to MM/DD/YYYY format
         */
        formatDate: (value) => {
            if (!value || value === '0001-01-01') return '';
            const parts = value.split('-');
            if (parts.length !== 3) return value;
            const [year, month, day] = parts;
            return `${month}/${day}/${year}`;
        },
        
        /**
         * Format price value (remove currency symbols, ensure 2 decimal places)
         */
        formatPrice: (value) => {
            if (!value) return '';
            // Remove any non-numeric characters except decimal point
            const cleaned = String(value).replace(/[^\d.]/g, '');
            const num = parseFloat(cleaned);
            return isNaN(num) ? '' : num.toFixed(2);
        }
    };

    /**
     * Main Application Class
     */
    class WarrantyClaimEditApp {
        constructor(customerId, apiEndpoints, categoriesMapping, settings) {
            this.customerId = customerId;
            this.apiEndpoints = apiEndpoints;
            this.categoriesMapping = categoriesMapping;
            this.settings = settings;
            this.orderId = null;
            this.warrantyData = null;
            this.validator = null;
            this.fileManager = null;
            
            // Parse editable statuses from CSV, convert to lowercase for comparison
            this.editableStatuses = (settings.editableStatuses || 'PENDING ADDITIONAL')
                .split(',')
                .map(s => s.trim().toLowerCase())
                .filter(s => s.length > 0);
            
            // DOM Elements
            this.loadingState = document.getElementById('loading-state');
            this.ownershipErrorState = document.getElementById('ownership-error-state');
            this.apiErrorState = document.getElementById('api-error-state');
            this.apiErrorMessage = document.getElementById('api-error-message');
            this.formContainer = document.getElementById('form-container');
            this.form = document.getElementById('product-registration-form');
        }

        async initialize() {
            // Get order_id from URL params
            const urlParams = new URLSearchParams(window.location.search);
            this.orderId = urlParams.get('order_id');
            
            if (!this.orderId) {
                this.showOwnershipError();
                return;
            }
            
            try {
                // Step 1: Verify ownership if enabled
                if (this.settings.enableOwnershipVerification) {
                    const isOwner = await this.verifyOwnership();
                    if (!isOwner) {
                        this.showOwnershipError();
                        return;
                    }
                }
                
                // Step 2: Fetch warranty claim details
                const claimData = await this.fetchWarrantyClaimDetails();
                if (!claimData) {
                    this.showApiError('Failed to load warranty claim details.');
                    return;
                }
                
                this.warrantyData = claimData;
                
                // Step 3: Initialize form components
                this.initializeFormComponents();
                
                // Step 4: Populate form with data
                this.populateForm(claimData);
                
                // Step 5: Show form
                this.showForm();
                
            } catch (error) {
                console.error('Initialization error:', error);
                this.showApiError(error.message || 'An unexpected error occurred.');
            }
        }

        /**
         * Verify that the customer owns this warranty claim and it has an editable status
         */
        async verifyOwnership() {
            try {
                const response = await fetch(this.apiEndpoints.GET_WARRANTY_CLAIMS, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify({ customerId: this.customerId })
                });
                
                if (!response.ok) {
                    console.error('Failed to fetch warranty claims:', response.status);
                    return false;
                }
                
                const data = await response.json();
                
                // Parse the nested JSON string in 'value'
                let claims = [];
                if (data && data.data && data.data.value) {
                    const parsed = JSON.parse(data.data.value);
                    claims = parsed.WarrantyClaims || [];
                }
                
                // Find the claim matching the order_id (normalize to strings for comparison)
                const targetOrderId = String(this.orderId);
                const matchingClaim = claims.find(claim => String(claim.order_id) === targetOrderId);
                
                if (!matchingClaim) {
                    console.error('No matching warranty claim found for order_id:', this.orderId);
                    return false;
                }
                
                // Check if status is editable (case-insensitive)
                const claimStatus = (matchingClaim.status || '').toLowerCase();
                const isEditableStatus = this.editableStatuses.includes(claimStatus);
                
                if (!isEditableStatus) {
                    console.error('Warranty status is not editable:', matchingClaim.status);
                    return false;
                }
                
                return true;
                
            } catch (error) {
                console.error('Error verifying ownership:', error);
                return false;
            }
        }

        /**
         * Fetch warranty claim details from the API
         */
        async fetchWarrantyClaimDetails() {
            try {
                const response = await fetch(this.apiEndpoints.GET_WARRANTY_CLAIM_DETAILS, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify({ orderId: this.orderId })
                });
                
                if (!response.ok) {
                    throw new Error(`API returned status ${response.status}`);
                }
                
                const data = await response.json();
                
                // The response has a 'value' array, get the first item
                if (data && data.data.value && data.data.value.length > 0) {
                    return data.data.value[0];
                }
                
                return null;
                
            } catch (error) {
                console.error('Error fetching warranty claim details:', error);
                throw error;
            }
        }

        /**
         * Initialize form components (validator, file manager, event listeners)
         */
        initializeFormComponents() {
            // Initialize validator and file manager
            this.validator = new WarrantyClaimsValidator();
            this.fileManager = new WarrantyClaimsFileManager(this.validator);
            this.fileManager.initialize();
            
            // Attach form event listeners
            this.attachEventListeners();
            this.initializeDatePicker();
            this.initializeCountrySelector();
            this.initializeCategorySelector();
        }

        /**
         * Populate form fields with warranty claim data
         */
        populateForm(data) {
            const lineItem = data.eShopCnctBWServiceOrderLineNew?.[0] || {};
            
            // Iterate through field mapping and populate each field
            Object.keys(FIELD_MAPPING).forEach(key => {
                const config = FIELD_MAPPING[key];
                const selector = config.formSelector;
                
                if (!selector) return;
                
                const element = document.querySelector(selector);
                if (!element) return;
                
                // Get value from appropriate source
                let value;
                if (config.isLineItem) {
                    value = lineItem[config.apiField];
                } else {
                    value = data[config.apiField];
                }
                
                // Apply transform if specified
                if (config.transform && TRANSFORMS[config.transform]) {
                    value = TRANSFORMS[config.transform](value);
                }
                
                // Set the value
                if (value !== undefined && value !== null) {
                    element.value = value;
                    
                    // Trigger change event for select elements to update dependent fields
                    if (element.tagName === 'SELECT') {
                        element.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                }
            });
            
            // After setting country, populate states and set state value
            const countrySelect = document.getElementById('country');
            const stateSelect = document.getElementById('state-province');
            if (countrySelect && stateSelect && data.shiptoCountryRegionCode) {
                // Trigger country change to populate states
                AddressDataManager.populateStates(data.shiptoCountryRegionCode, stateSelect);
                // Then set the state value
                if (data.county) {
                    stateSelect.value = data.county;
                }
            }
        }

        /**
         * Show the form container, hide loading state
         */
        showForm() {
            this.loadingState.style.display = 'none';
            this.ownershipErrorState.style.display = 'none';
            this.apiErrorState.style.display = 'none';
            this.formContainer.style.display = 'block';
        }

        /**
         * Show ownership verification error
         */
        showOwnershipError() {
            this.loadingState.style.display = 'none';
            this.ownershipErrorState.style.display = 'block';
            this.apiErrorState.style.display = 'none';
            this.formContainer.style.display = 'none';
        }

        /**
         * Show API/generic error
         */
        showApiError(message) {
            this.loadingState.style.display = 'none';
            this.ownershipErrorState.style.display = 'none';
            this.apiErrorState.style.display = 'block';
            this.formContainer.style.display = 'none';
            
            if (this.apiErrorMessage) {
                this.apiErrorMessage.textContent = message || 'There was an error loading your warranty claim. Please try again.';
            }
        }

        // ==================== Form Control Methods ====================
        // These are adapted from WarrantyClaimsFormController

        attachEventListeners() {
            const nextButtons = document.querySelectorAll('.next-step');
            const prevButtons = document.querySelectorAll('.prev-step');
            
            nextButtons.forEach(button => {
                button.addEventListener('click', () => this.handleNextStep(button));
            });

            prevButtons.forEach(button => {
                button.addEventListener('click', () => this.handlePrevStep(button));
            });

            this.form.addEventListener('submit', (e) => this.handleFormSubmit(e));
            this.form.addEventListener('input', (e) => this.handleLiveValidation(e));
            this.form.addEventListener('change', (e) => this.handleLiveValidation(e));
        }

        initializeDatePicker() {
            const purchaseDateInput = document.getElementById('purchase-date');
            if (!purchaseDateInput) return;

            purchaseDateInput.addEventListener('input', (e) => {
                e.target.value = DateUtils.formatWithSlashes(e.target.value);

                if (e.target.value.length === 10) {
                    const error = this.validator.validateDateField(e.target);
                    if (error) {
                        this.validator.showFieldError(e.target, error);
                    } else {
                        this.validator.clearFieldError(e.target);
                    }
                }
            });

            purchaseDateInput.addEventListener('blur', (e) => {
                const error = this.validator.validateDateField(e.target);
                if (error) {
                    this.validator.showFieldError(e.target, error);
                } else {
                    this.validator.clearFieldError(e.target);
                }
            });
        }

        initializeCountrySelector() {
            const countrySelector = document.getElementById('country');
            const stateProvinceSelector = document.getElementById('state-province');

            if (!countrySelector || !stateProvinceSelector) return;

            countrySelector.addEventListener('change', () => {
                const selectedCountry = countrySelector.value;
                AddressDataManager.populateStates(selectedCountry, stateProvinceSelector);
            });

            // Ensure states are populated if the country is pre-filled
            if (countrySelector.value) {
                AddressDataManager.populateStates(countrySelector.value, stateProvinceSelector);
            }
        }

        initializeCategorySelector() {
            const productCategorySelector = document.getElementById('product-category');
            const defectivePartSelector = document.getElementById('defective-part');
            const defectivePartWrapper = document.querySelector('.defective-part-wrap');

            if (!productCategorySelector || !defectivePartSelector || !defectivePartWrapper) return;

            productCategorySelector.addEventListener('change', () => {
                const selectedCategory = productCategorySelector.value;
                
                if (selectedCategory === '') {
                    defectivePartWrapper.style.display = 'none';
                } else {
                    defectivePartWrapper.style.display = 'block';
                    const selectedParts = this.categoriesMapping.find(
                        category => category.title === selectedCategory
                    );
                    
                    if (selectedParts && selectedParts.parts) {
                        defectivePartSelector.innerHTML = '<option value="">Please select</option>';
                        selectedParts.parts.forEach(part => {
                            const opt = document.createElement('option');
                            opt.value = part.code;
                            opt.textContent = part.title;
                            defectivePartSelector.appendChild(opt);
                        });
                    } else {
                        defectivePartSelector.innerHTML = '<option value="">Please select</option>';
                    }
                }
            });
        }

        handleNextStep(button) {
            const currentStep = parseInt(button.closest('.form-step').dataset.step);
            const nextStep = parseInt(button.dataset.next);

            if (this.validateStep(currentStep)) {
                this.showStep(nextStep);
                this.updateProgress(nextStep);
            }
        }

        handlePrevStep(button) {
            const prevStep = parseInt(button.dataset.prev);
            this.showStep(prevStep);
            this.updateProgress(prevStep);
        }

        handleLiveValidation(e) {
            const target = e.target;
            if (!target || !target.matches('input, textarea, select')) return;

            const formGroup = target.closest('.form-group') || target.parentElement;
            if (!formGroup) return;

            const hasExistingError = !!formGroup.querySelector('.error-message');
            if (!hasExistingError) return;

            const message = this.validator.validateField(target);
            if (message) {
                this.validator.showFieldError(target, message);
            } else {
                this.validator.clearFieldError(target);
            }
        }

        validateStep(stepNumber) {
            const currentStep = document.querySelector(`.form-step[data-step="${stepNumber}"]`);
            const fields = currentStep.querySelectorAll('input, select, textarea');
            let isValid = true;
            let firstInvalidField = null;

            fields.forEach(field => {
                const message = this.validator.validateField(field);
                if (message) {
                    this.validator.showFieldError(field, message);
                    if (!firstInvalidField) firstInvalidField = field;
                    isValid = false;
                } else {
                    this.validator.clearFieldError(field);
                }
            });

            if (!isValid && firstInvalidField) {
                try {
                    firstInvalidField.focus({ preventScroll: true });
                } catch (e) {
                    firstInvalidField.focus();
                }
                firstInvalidField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }

            return isValid;
        }

        showStep(stepNumber) {
            const steps = document.querySelectorAll('.form-step');
            steps.forEach(step => {step.classList.remove('active')});

            const targetSteps = document.querySelectorAll(`.form-step[data-step="${stepNumber}"]`);
            targetSteps.forEach(step => {step.classList.add('active')});

            if (stepNumber === 4) {
                this.updateReviewSection();
            }

            // Scroll to the top of the form
            const formProgress = document.querySelector('.form-progress');
            if (formProgress) {
                formProgress.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }

        updateProgress(currentStep) {
            const progressSteps = document.querySelectorAll('.progress-step');
            progressSteps.forEach((step, index) => {
                const stepNumber = index + 1;
                step.classList.remove('active', 'completed');

                if (stepNumber < currentStep) {
                    step.classList.add('completed');
                } else if (stepNumber === currentStep) {
                    step.classList.add('active');
                }
            });
        }

        // ==================== Review Section Methods ====================

        createEl(tag, className = null, text = null) {
            const el = document.createElement(tag);
            if (className) el.className = className;
            if (text !== null && text !== undefined) el.textContent = String(text);
            return el;
        }

        createReviewItem(label, value) {
            const item = this.createEl('div', 'review-item');
            item.append(
                this.createEl('span', 'review-label', `${label}:`),
                this.createEl('span', 'review-value', value)
            );
            return item;
        }

        clearAndAppend(parent, ...children) {
            if (!parent) return;
            parent.replaceChildren(...children.filter(Boolean));
        }

        updateReviewSection() {
            this.updateProductInfo();
            this.updateDocumentationInfo();
            this.updateContactInfo();
            this.updateAddressInfo();
        }

        updateProductInfo() {
            const productInfo = document.getElementById('review-product-info');
            if (!productInfo) return;

            const fields = [
                { label: 'Category', id: 'product-category' },
                { label: 'Subcategory', id: 'defective-part' },
                { label: 'Description', id: 'product-description' },
                { label: 'Purchase Date', id: 'purchase-date' },
                { label: 'Price', id: 'product-price', prefix: '$' },
                { label: 'Store', id: 'purchase-store' },
                { label: 'Serial Number', id: 'serial-number' }
            ];

            const items = fields.map(field => {
                const el = document.getElementById(field.id);
                const value = el?.value || 'Not provided';
                const displayValue = (() => {
                    if ((field.id === 'product-category' || field.id === 'defective-part') && el && el.tagName === 'SELECT') {
                        const selectedOption = el.selectedOptions?.[0];
                        const optionText = selectedOption?.textContent?.trim();
                        return optionText || value;
                    }
                    return field.prefix ? `${field.prefix}${value}` : value;
                })();
                return this.createReviewItem(field.label, displayValue);
            });

            this.clearAndAppend(productInfo, ...items);
        }

        updateDocumentationInfo() {
            const docsInfo = document.getElementById('review-docs-info');
            if (!docsInfo) return;

            const nodes = [];

            Object.keys(this.fileManager.fileUploads).forEach(type => {
                const fileData = this.fileManager.fileUploads[type];
                const typeLabel = type === 'receipt' ? 'Order Receipt Confirmation' : 
                                type === 'damage' ? 'Damage Photos' : 'Serial Number Photos';

                if (fileData.files.length === 0) {
                    nodes.push(this.createReviewItem(typeLabel, 'No files uploaded'));
                } else {
                    nodes.push(this.createReviewItem(typeLabel, `${fileData.files.length} file(s)`));

                    fileData.files.forEach(file => {
                        if (file.type && file.type.startsWith('image/')) {
                            const url = URL.createObjectURL(file);
                            const item = this.createEl('div', 'review-file-item');
                            const thumb = this.createEl('div', 'review-file-thumb');
                            const img = document.createElement('img');
                            img.src = url;
                            img.alt = file.name;
                            img.addEventListener('load', () => URL.revokeObjectURL(img.src));
                            thumb.appendChild(img);
                            item.append(thumb, this.createEl('span', 'review-file-name', file.name));
                            nodes.push(item);
                        } else {
                            nodes.push(this.createReviewItem('File', file.name));
                        }
                    });
                }
            });

            this.clearAndAppend(docsInfo, ...nodes);
        }

        updateContactInfo() {
            const personalInfo = document.getElementById('review-personal-info');
            if (!personalInfo) return;

            const fields = [
                { label: 'First Name', id: 'first-name' },
                { label: 'Last Name', id: 'last-name' },
                { label: 'Phone', id: 'phone' },
                { label: 'Email', id: 'email' }
            ];

            const items = fields.map(field => {
                const value = document.getElementById(field.id)?.value || 'Not provided';
                return this.createReviewItem(field.label, value);
            });

            this.clearAndAppend(personalInfo, ...items);
        }

        updateAddressInfo() {
            const addressInfo = document.getElementById('review-address');
            if (!addressInfo) return;

            const countryValue = document.getElementById('country')?.value;
            const countryDisplay =
                !countryValue || countryValue.trim() === ''
                    ? 'Not provided'
                    : countryValue === 'US'
                        ? 'United States'
                        : countryValue === 'CA'
                            ? 'Canada'
                            : countryValue;

            const stateProvinceEl = document.getElementById('state-province');
            const stateProvinceValue = stateProvinceEl?.value?.trim();
            const stateProvinceDisplay = (() => {
                if (!stateProvinceValue) return 'Not provided';
                if (stateProvinceEl && stateProvinceEl.tagName === 'SELECT') {
                    const selectedOption = stateProvinceEl.selectedOptions?.[0];
                    const optionText = selectedOption?.textContent?.trim();
                    return optionText || stateProvinceValue;
                }
                return stateProvinceValue;
            })();

            const items = [
                this.createReviewItem('Street Address', document.getElementById('street-address')?.value || 'Not provided'),
                this.createReviewItem('City', document.getElementById('city')?.value || 'Not provided'),
                this.createReviewItem('State/Province', stateProvinceDisplay),
                this.createReviewItem('Zip/Postal Code', document.getElementById('zip-postal')?.value || 'Not provided'),
                this.createReviewItem('Country', countryDisplay),
            ];

            this.clearAndAppend(addressInfo, ...items);
        }

        // ==================== Form Submission ====================

        createSpinner() {
            return this.createEl('span', 'spinner');
        }

        setButtonState(button, disabled, content, additionalClass = null) {
            if (!button) return;
            button.disabled = disabled;
            if (Array.isArray(content)) {
                button.replaceChildren(...content.filter(Boolean));
            } else if (content instanceof Node) {
                button.replaceChildren(content);
            } else if (content === null || content === undefined) {
                button.replaceChildren();
            } else {
                button.textContent = String(content);
            }
            if (additionalClass) {
                button.classList.add(additionalClass);
            }
        }

        async handleFormSubmit(e) {
            e.preventDefault();

            const submitBtn = e.target.querySelector('.btn-form-submit');
            
            // Prevent double submission - disable button and change type to prevent form resubmission
            if (submitBtn) {
                submitBtn.disabled = true;
                submitBtn.type = 'button';
                this.setButtonState(submitBtn, true, [this.createSpinner(), this.createEl('span', null, 'Submitting...')]);
            }

            try {
                // Re-verify ownership before submitting if verification is enabled
                if (this.settings.enableOwnershipVerification) {
                    const isStillOwner = await this.verifyOwnership();
                    if (!isStillOwner) {
                        this.showOwnershipErrorInForm();
                        return;
                    }
                }

                const formData = this.collectFormData();
                
                // Upload only files that have been added
                const fileUploads = {};
                
                if (this.hasFile('receipt')) {
                    const receiptUpload = await this.uploadFile('receipt');
                    fileUploads.receiptImageUrl = receiptUpload.fileName;
                }
                
                if (this.hasFile('damage')) {
                    const damageUpload = await this.uploadFile('damage');
                    fileUploads.damageImageUrl = damageUpload.fileName;
                }
                
                if (this.hasFile('serial')) {
                    const serialUpload = await this.uploadFile('serial');
                    fileUploads.batchImageUrl = serialUpload.fileName;
                }
                
                await this.updateWarrantyClaim(formData, fileUploads);

                this.showFinalStep(true);

            } catch (error) {
                console.error('Submission error:', error);
                this.showFinalStep(false, error.message);
            }
        }

        showFinalStep(isSuccess, errorMessage = null) {
            // Show step 5
            this.showStep(5);
            
            // Update progress step 4 with completed or failed
            const step4Progress = document.querySelector('.progress-step[data-step="4"]');
            if (step4Progress) {
                step4Progress.classList.remove('active');
                if (isSuccess) {
                    step4Progress.classList.add('completed');
                } else {
                    step4Progress.classList.add('failed');
                }
            }
            
            // Show appropriate message in the result container
            const resultContainer = document.querySelector('.form-step[data-step="5"] .result-message-container');
            if (resultContainer) {
                if (isSuccess) {
                    this.renderSuccessMessage(resultContainer);
                } else {
                    this.renderErrorMessage(resultContainer, errorMessage);
                }
            }
        }

        renderSuccessMessage(container) {
            const wrap = this.createEl('div', 'success-message');
            wrap.append(
                this.createEl('div', 'success-icon', '✓'),
                this.createEl('h3', null, 'Warranty Update Successful!'),
                this.createEl('p', null, 'Your warranty claim has been successfully updated. Thank you!')
            );
            this.clearAndAppend(container, wrap);
        }

        renderErrorMessage(container, errorMessage) {
            const wrap = this.createEl('div', 'error-message');
            const safeMessage = (errorMessage && typeof errorMessage === 'string' && errorMessage.trim())
                ? errorMessage.trim()
                : 'An unexpected error occurred';
            const errorDetails = this.createEl('p', 'error-details');
            errorDetails.textContent = `Error: ${safeMessage}`;

            wrap.append(
                this.createEl('div', 'error-icon', '✗'),
                this.createEl('h3', null, 'Warranty Update Failed'),
                this.createEl('p', null, 'Please refresh the page and try again or contact support.'),
                errorDetails
            );
            this.clearAndAppend(container, wrap);
        }

        collectFormData() {
            return {
                order_id: this.orderId,
                description: document.getElementById('product-description')?.value || 'Not provided',
                customer_id: this.customerId,
                purchase_date: DateUtils.convertToISOFormat(document.getElementById('purchase-date')?.value || ''),
                store: document.getElementById('purchase-store')?.value || 'Not provided',
                serialno: document.getElementById('serial-number')?.value || 'Not provided',
                price: document.getElementById('product-price')?.value || 'Not provided',
                serviceordertype: document.getElementById('defective-part')?.value || 'Not provided',
                first_name: document.getElementById('first-name')?.value || 'Not provided',
                last_name: document.getElementById('last-name')?.value || 'Not provided',
                Phone_No: document.getElementById('phone')?.value || 'Not provided',
                email: document.getElementById('email')?.value || 'Not provided',
                Address: document.getElementById('street-address')?.value || 'Not provided',
                County: document.getElementById('state-province')?.value || 'Not provided',
                City: document.getElementById('city')?.value || 'Not provided',
                Post_Code: document.getElementById('zip-postal')?.value || 'Not provided',
                country_id: document.getElementById('country')?.value || 'Not provided'
            };
        }

        /**
         * Check if a file has been added for a specific file type
         */
        hasFile(fileType) {
            const fileInput = document.getElementById(`${fileType}-files`);
            return fileInput && fileInput.files && fileInput.files.length > 0;
        }

        async uploadFile(fileType) {
            const fileInput = document.getElementById(`${fileType}-files`);
            if (!fileInput || !fileInput.files || fileInput.files.length === 0) {
                return null;
            }

            // Upload every file selected for this slot. For single-file slots this is
            // just one file; for multi-file slots (e.g. damage) the returned fileName
            // is a comma-separated string of all uploaded file URLs.
            const files = Array.from(fileInput.files);
            const uploads = await Promise.all(
                files.map(file => this.uploadSingleFile(file, fileType))
            );

            return { fileName: uploads.map(u => u.fileName).join(',') };
        }

        async uploadSingleFile(file, fileType) {
            const formData = new FormData();

            const uniqueId = `${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
            const lastDotIndex = file.name.lastIndexOf('.');
            const fileName = lastDotIndex !== -1
                ? `${file.name.substring(0, lastDotIndex)}_${uniqueId}${file.name.substring(lastDotIndex)}`
                : `${file.name}_${uniqueId}`;

            formData.append('image', file, fileName);

            const response = await fetch(this.apiEndpoints.AZURE_UPLOAD, {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                throw new Error(`${fileType} file upload failed: ${response.status}`);
            }

            return await response.json();
        }

        async updateWarrantyClaim(productData, fileUploads) {
            // Build request body with only the keys that have values
            const requestBody = {
                data: JSON.stringify(productData)
            };

            const orderId = this.orderId;
            if (orderId) {
                requestBody.orderId = orderId;
            }
            
            // Only add file keys if they were uploaded
            if (fileUploads.receiptImageUrl) {
                requestBody.receiptImageUrl = fileUploads.receiptImageUrl;
            }
            if (fileUploads.damageImageUrl) {
                requestBody.damageImageUrl = fileUploads.damageImageUrl;
            }
            if (fileUploads.batchImageUrl) {
                requestBody.batchImageUrl = fileUploads.batchImageUrl;
            }
            
            const response = await fetch(this.apiEndpoints.UPDATE_WARRANTY_CLAIM, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                throw new Error(`Warranty update failed: ${response.status}`);
            }

            return await response.json();
        }

        showOwnershipErrorInForm() {
            const summary = document.querySelector('.review-summary');
            if (!summary) return;

            const wrap = this.createEl('div', 'error-message');
            wrap.append(
                this.createEl('div', 'error-icon', '✗'),
                this.createEl('h3', null, 'Authorization Failed'),
                this.createEl('p', null, "This warranty doesn't exist or you are not logged in as the owner.")
            );

            const link = this.createEl('a', 'btn btn-primary', 'Go to Registration & Warranty');
            link.href = '/pages/registration-warranty';
            wrap.appendChild(link);

            this.clearAndAppend(summary, wrap);
        }
    }

    // Export to global scope for initialization from Liquid template
    window.WarrantyClaimEditApp = WarrantyClaimEditApp;

})();
